import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { FlaskConical, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{label}</p>
    <p className="text-lg font-bold text-white tabular-nums" data-testid={testid}>{value}</p>
  </div>
);

const GroupTable = ({ title, rows }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <p className="text-sm font-semibold text-white mb-2">{title}</p>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] uppercase text-white/40">
          <th className="text-left py-1">Group</th><th className="text-right">Pairs</th>
          <th className="text-right">Mean Δ</th><th className="text-right">Within ±5</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-t border-white/10">
            <td className="py-1.5 text-white/80">{r.name}</td>
            <td className="py-1.5 text-right text-white/60">{r.pairs}</td>
            <td className={`py-1.5 text-right tabular-nums ${Math.abs(r.mean_delta) > 1 ? 'text-amber-300' : 'text-white/80'}`}>{r.mean_delta > 0 ? '+' : ''}{r.mean_delta}</td>
            <td className={`py-1.5 text-right tabular-nums ${r.within_5_pct < 90 ? 'text-amber-300' : 'text-emerald-400'}`}>{r.within_5_pct}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminRebaselineReviewPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/grading/rebaseline-review`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load review pack'));
  }, []);

  if (!data) return <AdminLayout><div className="p-6 text-white/40">Loading…</div></AdminLayout>;
  const gate = data.gate || {};

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="rebaseline-review-page">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FlaskConical className="w-6 h-6 text-teal-400" /> Rebaseline Review — shadow v2 vs live v1
        </h1>
        <p className="text-xs text-white/40">
          Every live grade is double-graded with the v2 split/cached layout. This pack decides whether v2 can
          replace v1 (cutting off the shadow cost). Window: {data.window_start?.slice(0, 10)} → closes {data.window_closes?.slice(0, 10)} ·
          day {data.window_days_elapsed} of {data.window_days_total}{data.window_complete ? ' — WINDOW COMPLETE' : ''}
        </p>

        <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${gate.passed ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/15'}`} data-testid="rebaseline-gate-card">
          {gate.passed
            ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            : <XCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />}
          <div>
            <p className={`font-semibold ${gate.passed ? 'text-emerald-400' : (gate.post_fix_pairs ?? 0) < (gate.min_pairs ?? 30) ? 'text-amber-300' : 'text-red-300'}`}>
              Equivalence gate: {gate.passed ? 'PASS' : (gate.post_fix_pairs ?? 0) < (gate.min_pairs ?? 30) ? 'COLLECTING' : 'FAIL'}
              <span className="text-xs font-normal text-white/50 ml-2">
                (post-fix pairs only: {gate.post_fix_pairs ?? 0}/{gate.min_pairs ?? 30} · mean Δ {gate.post_fix_mean_delta ?? '—'} · {gate.post_fix_within_5_pct ?? '—'}% within ±5)
              </span>
            </p>
            <p className="text-xs text-white/70 mt-1" data-testid="rebaseline-recommendation">{data.recommendation}</p>
            {data.layout && <p className="text-[11px] text-white/40 mt-1" data-testid="rebaseline-layout-note">Layout: <span className="font-mono">{data.layout}</span> — window auto-restarts whenever the layout version changes</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Pairs" value={data.pairs} testid="rb-pairs" />
          <Stat label="Mean Δ" value={`${data.mean_delta > 0 ? '+' : ''}${data.mean_delta}`} testid="rb-mean" />
          <Stat label="Median Δ" value={data.median_delta} testid="rb-median" />
          <Stat label="Within ±2" value={`${data.within_2_pct}%`} testid="rb-w2" />
          <Stat label="Within ±5" value={`${data.within_5_pct}%`} testid="rb-w5" />
          <Stat label="|Δ| ≥ 8" value={data.over_8_count} testid="rb-o8" />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <GroupTable title="By tenant" rows={data.by_domain || []} />
          <GroupTable title="By call length" rows={data.by_duration || []} />
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <p className="text-sm font-semibold text-white mb-2">Worst 10 disagreements</p>
          <div className="space-y-1" data-testid="rebaseline-outliers">
            {(data.outliers || []).map((o) => (
              <div key={o.call_id} className="flex items-center justify-between gap-2 border-t border-white/10 py-1.5 text-xs flex-wrap">
                <span className="text-white/70">{o.agent_name || '?'} · {o.domain} · {o.duration_seconds ?? '?'}s</span>
                <span className="tabular-nums">
                  <span className="text-white/50">v1 {o.live_score}</span>
                  <span className="text-white/30 mx-1">→</span>
                  <span className="text-white/80">v2 {o.v2_score}</span>
                  <span className={`ml-2 font-bold ${Math.abs(o.delta) >= 8 ? 'text-red-300' : 'text-amber-300'}`}>{o.delta > 0 ? '+' : ''}{o.delta}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
